import { FC, ReactNode } from 'react';

import { VoucherDetails } from '@/api/voucher/voucher.types.ts';
import CustomGrid from '@/components/controls/CustomGrid.tsx';
import CustomGridLabelValueRow, {
  LabelValueGridRow,
} from '@/components/controls/CustomGridLabelValueRow.tsx';
import { formatDateFromUnix } from '@/utils/dateUtil.ts';
import { capitalizeText } from '@/utils/valueFormatter.ts';

type VoucherDetailsGridProps = {
  voucherDetails: VoucherDetails;
  isAdmin?: boolean;
};

const VoucherDetailsGrid: FC<VoucherDetailsGridProps> = ({
  voucherDetails,
  isAdmin = false,
}: VoucherDetailsGridProps) => {
  const gridData: LabelValueGridRow[] = [
    {
      id: 'name',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Event Name:',
      labelStyle: 'w-1/3',
      value: voucherDetails.name,
      valueStyle: 'w-2/3',
    },
    {
      id: 'code',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Code:',
      labelStyle: 'w-1/3',
      value: voucherDetails.code,
      valueStyle: 'w-2/4',
    },
    {
      id: 'status',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Status:',
      labelStyle: 'w-1/3',
      value: voucherDetails.status,
      transform: capitalizeText,
      valueStyle: 'w-2/3 text-lg font-bold',
    },
    ...(isAdmin
      ? [
          {
            id: 'companyName',
            rowStyle: 'col-span-1 flex flex-row',
            label: 'Issued by:',
            labelStyle: 'w-1/3',
            value: voucherDetails.companyName ?? '',
            valueStyle: 'w-2/3',
            noValueText: 'N/A',
          },
          {
            id: 'companyEmail',
            rowStyle: 'col-span-1 flex flex-row',
            label: 'Email Address:',
            labelStyle: 'w-1/3',
            value: voucherDetails.companyEmail ?? '',
            valueStyle: 'w-2/3 break-all',
            noValueText: 'N/A',
          },
        ]
      : []),
    {
      id: 'type',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Type:',
      labelStyle: 'w-1/3',
      value: voucherDetails.type,
      transform: capitalizeText,
      valueStyle: 'w-2/3',
    },
    {
      id: 'dateCreated',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Date Created:',
      labelStyle: 'w-1/3',
      value: voucherDetails.timeOfPurchaseUnix,
      transform: formatDateFromUnix,
      valueStyle: 'w-2/3',
    },
    {
      id: 'rewardTierPrize',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Reward Tier:',
      labelStyle: 'w-1/3',
      value: `R${voucherDetails.rewardTierPrize}`,
      valueStyle: 'w-2/3',
    },
    {
      id: 'validFor',
      rowStyle: 'col-span-1 flex flex-row',
      label: 'Event Date:',
      labelStyle: 'w-1/3',
      value: voucherDetails.validFromUnix,
      transform: formatDateFromUnix,
      valueStyle: 'w-2/3',
    },
  ];

  const voucherDetailsGridItem: ReactNode[] = gridData.map((item) => (
    <CustomGridLabelValueRow
      key={item.id}
      id={item.id}
      rowStyle={item.rowStyle}
      label={item.label}
      labelStyle={item.labelStyle}
      value={item.transform ? item.transform(item.value) : item.value}
      noValueText={item.noValueText}
      valueStyle={item.valueStyle}
    />
  ));

  return (
    <CustomGrid
      items={voucherDetailsGridItem}
      cols={1}
      gap={4}
      className="mt-4 mb-4"
    />
  );
};

export default VoucherDetailsGrid;
